const chapter = {
  id: 'probability',
  slug: 'probability',
  subject: 'mathematics',
  subjectLabel: 'Mathematics',
  exam: 'JEE Main + Advanced',
  title: {
    english: 'Probability',
        hindi: 'प्रायिकता',
  },
  estReadMinutes: 60,
  difficulty: {
    english: 'Medium–Hard',
        hindi: 'मध्यम-कठोर',
  },
  weightage: {
    english: '2–3 questions in JEE Main',
        hindi: 'जेईई मेन में 2-3 प्रश्न',
  },
  syllabusTags: ['Probability', 'JEE-Main', 'JEE-Advanced', 'Class-12'],
  sections: [
    {
      id: 'basics',
      title: {
        english: 'Sample Space, Events & Axioms',
        hindi: 'प्रतिदर्श समष्टि, घटनाएँ और अभिगृहीत',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Sample space & event',
        hindi: 'नमूना स्थान और घटना',
          },
          body: {
            english: '**Sample space** $S$: set of all possible outcomes. **Event** $A$: subset of $S$. **Elementary** event: single outcome. **Sure** event: $S$ itself. **Impossible** event: $\\emptyset$.',
        hindi: '**नमूना स्थान** $S$: सभी संभावित परिणामों का सेट। **घटना** $A$: $S$ का सबसेट। **प्राथमिक** घटना: एकल परिणाम। **निश्चित** घटना: $S$ ही। **असंभव** घटना: $\\emptyset$।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Mutually exclusive**: $A \\cap B = \\emptyset$.',
        hindi: '**परस्पर अनन्य**: $A \\cap B = \\emptyset$।',
            },
            {
              english: '**Exhaustive**: $A_1 \\cup A_2 \\cup \\cdots \\cup A_n = S$.',
        hindi: '**संपूर्ण**: $A_1 \\cup A_2 \\cup \\cdots \\cup A_n = S$.',
            },
            {
              english: '**Independent**: $P(A \\cap B) = P(A) P(B)$.',
        hindi: '**स्वतंत्र**: $P(A \\cap B) = P(A) P(B)$.',
            },
            {
              english: 'Complement: $A^c$, $P(A^c) = 1 - P(A)$.',
        hindi: 'पूरक: $A^c$, $P(A^c) = 1 - P(A)$.',
            },
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Axioms of probability',
        hindi: 'संभाव्यता के अभिगृहीत',
          },
          statement: {
            english: '(i) $0 \\le P(A) \\le 1$. (ii) $P(S) = 1$. (iii) For pairwise disjoint events $A_i$: $P(\\bigcup A_i) = \\sum P(A_i)$.',
        hindi: '(i) $0 \\le P(A) \\le 1$. (ii) $P(S) = 1$. (iii) जोड़ीवार असंयुक्त घटनाओं के लिए $A_i$: $P(\\bigcup A_i) = \\sum P(A_i)$।',
          },
        },
      ],
    },
    {
      id: 'addition',
      title: {
        english: 'Addition Theorem & Inclusion–Exclusion',
        hindi: 'योग प्रमेय और समावेश-अपवर्जन',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Addition theorem',
        hindi: 'जोड़ प्रमेय',
          },
          statement: {
            english: `$P(A \\cup B) = P(A) + P(B) - P(A \\cap B).$
For three events:
$P(A \\cup B \\cup C) = P(A) + P(B) + P(C) - P(A\\cap B) - P(B\\cap C) - P(C\\cap A) + P(A\\cap B\\cap C).$`,
            hindi: '$P(A \\cup B) = P(A) + P(B) - P(A \\cap B).$ तीन आयोजनों के लिए: $P(A \\cup B \\cup C) = P(A) + P(B) + P(C) - P(A\\cap B) - P(B\\cap C) - P(C\\cap A) + P(A\\cap B\\cap C).$',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '$P(\\text{exactly one}) = P(A) + P(B) - 2P(A \\cap B)$ (two events).',
        hindi: '$P(\\text{exactly one}) = P(A) + P(B) - 2P(A \\cap B)$ (दो घटनाएँ)।',
            },
            {
              english: '$P(\\text{neither}) = 1 - P(A \\cup B)$.',
        hindi: '$P(\\text{neither}) = 1 - P(A \\cup B)$.',
            },
            {
              english: '$P(A \\cap B^c) = P(A) - P(A \\cap B)$.',
        hindi: '$P(A \\cap B^c) = P(A) - P(A \\cap B)$.',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'A card is drawn from a 52-card deck. Probability it is red **or** a king?',
        hindi: '52-कार्ड डेक से एक कार्ड निकाला जाता है। संभावना है कि यह लाल **या** राजा है?',
          },
          solution: {
            english: '$P(R) = 26/52, P(K) = 4/52, P(R \\cap K) = 2/52$. $P(R \\cup K) = (26+4-2)/52 = 28/52 = 7/13$.',
        hindi: '$P(R) = 26/52, P(K) = 4/52, P(R \\cap K) = 2/52$. $P(R \\cup K) = (26+4-2)/52 = 28/52 = 7/13$.',
          },
        },
      ],
    },
    {
      id: 'conditional',
      title: {
        english: 'Conditional Probability & Multiplication',
        hindi: 'सशर्त प्रायिकता और गुणन',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Conditional probability',
        hindi: 'सशर्त संभाव्यता',
          },
          body: {
            english: '$P(A \\mid B) = \\dfrac{P(A \\cap B)}{P(B)}$ provided $P(B) > 0$. Reads "probability of $A$ given $B$".',
        hindi: '$P(A \\mid B) = \\dfrac{P(A \\cap B)}{P(B)}$ ने $P(B) > 0$ प्रदान किया। "$A$ की संभावना $B$ दी गई है" पढ़ता है।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Multiplication rule',
        hindi: 'गुणन नियम',
          },
          statement: {
            english: `$P(A \\cap B) = P(B) P(A \\mid B) = P(A) P(B \\mid A)$.
For independent events: $P(A \\cap B) = P(A) P(B)$, equivalently $P(A \\mid B) = P(A)$.`,
            hindi: '$P(A \\cap B) = P(B) P(A \\mid B) = P(A) P(B \\mid A)$। स्वतंत्र आयोजनों के लिए: $P(A \\cap B) = P(A) P(B)$, समकक्ष $P(A \\mid B) = P(A)$।',
          },
        },
        {
          type: 'tip',
          text: {
            english: 'Pairwise independence does NOT imply mutual independence. For three events to be mutually independent, all $\\binom{3}{2}$ pairwise products **and** the triple product must factorize.',
        hindi: 'जोड़ीवार स्वतंत्रता का अर्थ पारस्परिक स्वतंत्रता नहीं है। तीन घटनाओं के परस्पर स्वतंत्र होने के लिए, सभी $\\binom{3}{2}$ जोड़ीवार उत्पाद **और** ट्रिपल उत्पाद को गुणनखंडित करना होगा।',
          },
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Two cards drawn without replacement. Probability both are aces?',
        hindi: 'बिना प्रतिस्थापन के दो कार्ड निकाले गए। प्रायिकता दोनों इक्के हैं?',
          },
          solution: {
            english: '$P(A_1) \\cdot P(A_2 \\mid A_1) = \\dfrac{4}{52} \\cdot \\dfrac{3}{51} = \\dfrac{1}{221}$.',
        hindi: '$P(A_1) \\cdot P(A_2 \\mid A_1) = \\dfrac{4}{52} \\cdot \\dfrac{3}{51} = \\dfrac{1}{221}$.',
          },
        },
      ],
    },
    {
      id: 'total-bayes',
      title: {
        english: 'Total Probability & Bayes\' Theorem',
        hindi: 'कुल संभाव्यता और बेयस प्रमेय',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Total probability',
        hindi: 'कुल संभावना',
          },
          statement: {
            english: `If $E_1, E_2, \\ldots, E_n$ form a partition of $S$ (mutually exclusive & exhaustive) and $A$ is any event:
$$P(A) = \\sum_{i=1}^n P(E_i) P(A \\mid E_i).$$`,
            hindi: 'यदि $E_1, E_2, \\ldots, E_n$ $S$ (परस्पर अनन्य और संपूर्ण) और $A$ का एक विभाजन बनाते हैं तो कोई घटना है: $$P(A) = \\sum_{i=1}^n P(E_i) P(A \\mid E_i).$$',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Bayes\' theorem',
        hindi: 'बेयस प्रमेय',
          },
          statement: {
            english: `Under the same setup,
$$P(E_k \\mid A) = \\dfrac{P(E_k) P(A \\mid E_k)}{\\sum_{i=1}^n P(E_i) P(A \\mid E_i)}.$$
$P(E_k)$ are **prior** probabilities; $P(E_k \\mid A)$ are **posterior** probabilities.`,
            hindi: 'उसी सेटअप के तहत, $$P(E_k \\mid A) = \\dfrac{P(E_k) P(A \\mid E_k)}{\\sum_{i=1}^n P(E_i) P(A \\mid E_i)}.$$ $P(E_k)$ **पूर्व** संभावनाएं हैं; $P(E_k \\mid A)$ **पिछली** संभावनाएं हैं।',
          },
        },
        {
          type: 'example',
          number: 3,
          problem: {
            english: 'Bag I has 3 W, 4 B balls. Bag II has 5 W, 2 B. A bag is chosen at random and a ball drawn turns out white. Probability it came from Bag II?',
        hindi: 'बैग I में 3 W, 4 B गेंदें हैं। बैग II में 5 W, 2 B हैं। एक बैग यादृच्छिक रूप से चुना जाता है और निकाली गई गेंद सफेद निकलती है। संभावना है कि यह बैग II से आया है?',
          },
          solution: {
            english: '$P(I) = P(II) = 1/2$. $P(W|I) = 3/7, P(W|II) = 5/7$. $P(II|W) = \\dfrac{(1/2)(5/7)}{(1/2)(3/7) + (1/2)(5/7)} = \\dfrac{5}{8}$.',
        hindi: '$P(I) = P(II) = 1/2$. $P(W|I) = 3/7, P(W|II) = 5/7$. $P(II|W) = \\dfrac{(1/2)(5/7)}{(1/2)(3/7) + (1/2)(5/7)} = \\dfrac{5}{8}$.',
          },
        },
      ],
    },
    {
      id: 'random-vars',
      title: {
        english: 'Random Variables, Mean & Variance',
        hindi: 'यादृच्छिक चर, माध्य और प्रसरण',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Random variable & expectation',
        hindi: 'यादृच्छिक चर और अपेक्षा',
          },
          body: {
            english: `A **discrete random variable** $X$ takes values $x_1, x_2, \\ldots$ with probabilities $p_1, p_2, \\ldots$ where $\\sum p_i = 1$.
• **Mean (expectation)**: $E(X) = \\mu = \\sum x_i p_i$.
• **Variance**: $\\text{Var}(X) = E(X^2) - [E(X)]^2 = \\sum (x_i - \\mu)^2 p_i$.
• **Std deviation**: $\\sigma = \\sqrt{\\text{Var}(X)}$.`,
            hindi: 'एक **असतत यादृच्छिक चर** $X$ संभावनाओं $p_1, p_2, \\ldots$ के साथ $x_1, x_2, \\ldots$ मान लेता है जहां $\\sum p_i = 1$ होता है। • **मीन (उम्मीद)**: $E(X) = \\mu = \\sum x_i p_i$. • **विचरण**: $\\text{Var}(X) = E(X^2) - [E(X)]^2 = \\sum (x_i - \\mu)^2 p_i$. • **एसटीडी विचलन**: $\\sigma = \\sqrt{\\text{Var}(X)}$।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '$E(aX + b) = a E(X) + b$',
        hindi: '$E(aX + b) = a E(X) + b$',
            },
            {
              english: '$\\text{Var}(aX + b) = a^2 \\text{Var}(X)$',
        hindi: '$\\text{Var}(aX + b) = a^2 \\text{Var}(X)$',
            },
            {
              english: 'For independent $X, Y$: $E(XY) = E(X)E(Y)$ and $\\text{Var}(X+Y) = \\text{Var}(X) + \\text{Var}(Y)$.',
        hindi: 'स्वतंत्र $X, Y$ के लिए: $E(XY) = E(X)E(Y)$ और $\\text{Var}(X+Y) = \\text{Var}(X) + \\text{Var}(Y)$।',
            },
          ],
        },
      ],
    },
    {
      id: 'binomial',
      title: {
        english: 'Bernoulli Trials & Binomial Distribution',
        hindi: 'बर्नूली परीक्षण और द्विपद बंटन',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Binomial distribution',
        hindi: 'द्विपद वितरण',
          },
          statement: {
            english: `For $n$ independent Bernoulli trials with success probability $p$ (failure $q = 1 - p$), the number of successes $X \\sim B(n, p)$ has:
$$P(X = r) = \\binom{n}{r} p^r q^{n - r},\\quad r = 0, 1, \\ldots, n.$$
**Mean** $= np$. **Variance** $= npq$ ($\\le np$, since $q \\le 1$).`,
            hindi: 'सफलता की संभावना $p$ (विफलता $q = 1 - p$) के साथ $n$ स्वतंत्र बर्नौली परीक्षणों के लिए, सफलताओं की संख्या $X \\sim B(n, p)$ है: $$P(X = r) = \\binom{n}{r} p^r q^{n - r},\\quad r = 0, 1, \\ldots, n.$$ **मतलब** $= np$. **विचरण** $= npq$ ($\\le np$, चूँकि $q \\le 1$)।',
          },
        },
        {
          type: 'example',
          number: 4,
          problem: {
            english: 'A die is thrown 5 times. Probability of getting exactly 2 sixes?',
        hindi: 'एक पासे को 5 बार फेंका जाता है। ठीक 2 छक्के लगने की संभावना?',
          },
          solution: {
            english: '$p = 1/6, q = 5/6$. $P(X = 2) = \\binom{5}{2}(1/6)^2 (5/6)^3 = 10 \\cdot \\dfrac{125}{7776} = \\dfrac{1250}{7776} = \\dfrac{625}{3888}$.',
        hindi: '$p = 1/6, q = 5/6$. $P(X = 2) = \\binom{5}{2}(1/6)^2 (5/6)^3 = 10 \\cdot \\dfrac{125}{7776} = \\dfrac{1250}{7776} = \\dfrac{625}{3888}$.',
          },
        },
        {
          type: 'tip',
          text: {
            english: 'For "at least one" problems use complement: $P(X \\ge 1) = 1 - P(X = 0) = 1 - q^n$.',
        hindi: '"कम से कम एक" समस्या के लिए पूरक का उपयोग करें: $P(X \\ge 1) = 1 - P(X = 0) = 1 - q^n$।',
          },
        },
      ],
    },
    {
      id: 'tricks',
      title: {
        english: 'Useful Tricks & Common Setups',
        hindi: 'उपयोगी युक्तियाँ और सामान्य संरचनाएँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Symmetry**: e.g., probability that a random arrangement places A before B is $1/2$.',
        hindi: '**समरूपता**: उदाहरण के लिए, एक यादृच्छिक व्यवस्था में A को B से पहले रखने की संभावना $1/2$ है।',
            },
            {
              english: '**At least / at most**: convert to complement when fewer cases.',
        hindi: '**कम से कम / अधिक से अधिक**: कम मामले होने पर पूरक में कनवर्ट करें।',
            },
            {
              english: '**Geometric probability**: ratio of favourable region to total region (length, area, volume).',
        hindi: '**ज्यामितीय संभाव्यता**: अनुकूल क्षेत्र का कुल क्षेत्र से अनुपात (लंबाई, क्षेत्रफल, आयतन)। **संयोगी जन्मदिन/मिलान संबंधी समस्याएं**: समावेशन-बहिष्करण या पूरक का उपयोग करें। **प्रतिस्थापन के बिना ड्राइंग** बनाम **प्रतिस्थापन के साथ**: उत्तरार्द्ध स्वतंत्रता देता है।',
            },
            {
              english: '**Coincident birthdays / matching problems**: use inclusion–exclusion or complement.',
        hindi: '**संयोगी जन्मदिन/मिलान समस्याएँ**: समावेशन-बहिष्करण या पूरक का उपयोग करें।',
            },
            {
              english: '**Drawing without replacement** vs **with replacement**: latter gives independence.',
        hindi: '**प्रतिस्थापन के बिना ड्राइंग** बनाम **प्रतिस्थापन के साथ**: उत्तरार्द्ध स्वतंत्रता देता है।',
            },
          ],
        },
        {
          type: 'example',
          number: 5,
          problem: {
            english: 'A fair coin is tossed 10 times. Probability of getting at least one head?',
        hindi: 'एक निष्पक्ष सिक्के को 10 बार उछाला जाता है। कम से कम एक सिर मिलने की संभावना?',
          },
          solution: {
            english: '$1 - (1/2)^{10} = 1 - 1/1024 = 1023/1024$.',
        hindi: '$1 - (1/2)^{10} = 1 - 1/1024 = 1023/1024$।',
          },
        },
        {
          type: 'example',
          number: 6,
          problem: {
            english: 'Three persons A, B, C in that order toss a coin; first to get a head wins. Find $P(A \\text{ wins})$.',
        hindi: 'तीन व्यक्ति A, B, C इसी क्रम में एक सिक्का उछालते हैं; सबसे पहले सिर पाने वाला जीतता है। $P(A \\text{ wins})$ खोजें।',
          },
          solution: {
            english: '$P(A) = \\tfrac12 + \\tfrac12 \\cdot \\tfrac12 \\cdot \\tfrac12 \\cdot \\tfrac12 + \\cdots = \\dfrac{1/2}{1 - 1/8} = \\dfrac{4}{7}$.',
        hindi: '$P(A) = \\tfrac12 + \\tfrac12 \\cdot \\tfrac12 \\cdot \\tfrac12 \\cdot \\tfrac12 + \\cdots = \\dfrac{1/2}{1 - 1/8} = \\dfrac{4}{7}$.',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'P(A \\cup B) = P(A) + P(B) - P(A \\cap B)',
      desc: {
        english: 'Addition',
        hindi: 'अतिरिक्त',
      },
    },
    {
      latex: 'P(A | B) = P(A \\cap B) / P(B)',
      desc: {
        english: 'Conditional',
        hindi: 'सशर्त',
      },
    },
    {
      latex: 'P(A \\cap B) = P(A) P(B)',
      desc: {
        english: 'Independence',
        hindi: 'स्वतंत्रता',
      },
    },
    {
      latex: 'P(E_k|A) = \\tfrac{P(E_k)P(A|E_k)}{\\sum P(E_i) P(A|E_i)}',
      desc: {
        english: 'Bayes',
        hindi: 'बेयस',
      },
    },
    {
      latex: 'P(X=r) = \\binom{n}{r} p^r q^{n-r}',
      desc: {
        english: 'Binomial pmf',
        hindi: 'द्विपद पीएमएफ',
      },
    },
    {
      latex: 'E(X) = np,\\ \\text{Var}(X) = npq',
      desc: {
        english: 'Binomial moments',
        hindi: 'द्विपद क्षण',
      },
    },
    {
      latex: '\\text{Var}(X) = E(X^2) - [E(X)]^2',
      desc: {
        english: '',
        hindi: 'वार(एक्स) = ई(एक्स 2) - [ई(एक्स)] 2',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Bayes',
      problem: {
        english: 'Box I has 5 R, 3 B; Box II has 4 R, 5 B. Box chosen at random; one ball drawn is red. Probability it came from Box I?',
        hindi: 'बॉक्स I में 5 आर, 3 बी हैं; बॉक्स II में 4 R, 5 B हैं। बॉक्स को यादृच्छिक रूप से चुना गया है; निकाली गई एक गेंद लाल है। संभावना है कि यह बॉक्स I से आया है?',
      },
      answer: {
        english: '$\\dfrac{45}{77}$',
        hindi: '$\\dfrac{45}{77}$',
      },
      solution: {
        english: '$P(I|R) = \\dfrac{(1/2)(5/8)}{(1/2)(5/8) + (1/2)(4/9)} = \\dfrac{5/16}{5/16 + 4/18} = \\dfrac{5/16}{45/144 + 32/144} = \\dfrac{5/16 \\cdot 144}{77} = \\dfrac{45}{77}$.',
        hindi: '$P(I|R) = \\dfrac{(1/2)(5/8)}{(1/2)(5/8) + (1/2)(4/9)} = \\dfrac{5/16}{5/16 + 4/18} = \\dfrac{5/16}{45/144 + 32/144} = \\dfrac{5/16 \\cdot 144}{77} = \\dfrac{45}{77}$.',
      },
    },
    {
      year: 2023,
      exam: 'JEE Main',
      tag: 'Binomial',
      problem: {
        english: 'Probability that a randomly selected page from a book has a misprint is $0.05$. Probability that out of 4 pages, at least one has a misprint?',
        hindi: 'किसी पुस्तक के बेतरतीब ढंग से चुने गए पृष्ठ पर गलत छपाई होने की संभावना $0.05$ है। संभावना है कि 4 पेजों में से कम से कम एक पेज गलत छपा हो?',
      },
      answer: {
        english: '$1 - (0.95)^4 \\approx 0.1855$',
        hindi: '$1 - (0.95)^4 \\approx 0.1855$',
      },
      solution: {
        english: 'Complement of "no misprints in any of 4 pages".',
        hindi: '"4 पृष्ठों में से किसी में भी कोई गलत छाप नहीं" का पूरक।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Conditional',
      problem: {
        english: 'A die is rolled. Given that the outcome is even, probability it is greater than 3?',
        hindi: 'एक पासा फेंका जाता है। यह देखते हुए कि परिणाम सम है, प्रायिकता यह 3 से अधिक है?',
      },
      answer: {
        english: '$2/3$',
        hindi: '$2/3$',
      },
      solution: {
        english: 'Sample space $\\{2, 4, 6\\}$; favourable $\\{4, 6\\}$.',
        hindi: 'नमूना स्थान $\\{2, 4, 6\\}$; अनुकूल $\\{4, 6\\}$.',
      },
    },
    {
      year: 2021,
      exam: 'JEE Main',
      tag: 'Total probability',
      problem: {
        english: '40% of items produced by machine I, 60% by machine II. 5% of I and 3% of II are defective. A randomly chosen item is defective; probability it came from machine II?',
        hindi: '40% आइटम मशीन I द्वारा उत्पादित होते हैं, 60% मशीन II द्वारा। I का 5% और II का 3% दोषपूर्ण है। बेतरतीब ढंग से चुनी गई वस्तु दोषपूर्ण है; सम्भावना यह है कि यह मशीन II से आया है?',
      },
      answer: {
        english: '$\\dfrac{18}{38} = \\dfrac{9}{19}$',
        hindi: '$\\dfrac{18}{38} = \\dfrac{9}{19}$',
      },
      solution: {
        english: 'Bayes: $\\dfrac{0.6 \\cdot 0.03}{0.4 \\cdot 0.05 + 0.6 \\cdot 0.03} = \\dfrac{0.018}{0.038}$.',
        hindi: 'बेयस: $\\dfrac{0.6 \\cdot 0.03}{0.4 \\cdot 0.05 + 0.6 \\cdot 0.03} = \\dfrac{0.018}{0.038}$.',
      },
    },
    {
      year: 2020,
      exam: 'JEE Advanced',
      tag: 'Independence',
      problem: {
        english: 'Two events $A, B$ with $P(A) = 1/4, P(A \\cup B) = 1/2$. If they are independent, find $P(B)$.',
        hindi: '$P(A) = 1/4, P(A \\cup B) = 1/2$ के साथ दो इवेंट $A, B$। यदि वे स्वतंत्र हैं, तो $P(B)$ खोजें।',
      },
      answer: {
        english: '$1/3$',
        hindi: '$1/3$',
      },
      solution: {
        english: '$P(A \\cup B) = P(A) + P(B) - P(A)P(B) \\Rightarrow 1/2 = 1/4 + P(B) - P(B)/4 \\Rightarrow 1/4 = (3/4) P(B) \\Rightarrow P(B) = 1/3$.',
        hindi: '$P(A \\cap बी) = पी(ए) + पी(बी) - पी(ए)पी(बी) \\Rightarrow 1/2 = 1/4 + पी(बी) - पी(बी)/4 \\Rightarrow 1/4 = (3/4) पी(बी) \\Rightarrow पी(बी) = 1/3$।',
      },
    },
  ],
  next: {
    slug: 'statistics-reasoning',
    title: 'Statistics & Mathematical Reasoning',
  },
};

export default chapter;
